/**
 * GlassBoxPanel — Glass Box observability event timeline (Spec 124 Phase 2)
 *
 * Collapsible bottom panel showing real-time agent loop observability events.
 * Wired to UserWebSocket via useGlassBoxEvents hook.
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Trash2, ChevronDown, History, Loader2 } from 'lucide-react';
import { LLMCallEvent } from './events/LLMCallEvent';
import { ToolCallEvent } from './events/ToolCallEvent';
import { ErrorEvent } from './events/ErrorEvent';
import { GenericObsEvent } from './events/GenericObsEvent';
import type { GlassBoxItem, GlassBoxFilter } from '../../hooks/useGlassBoxEvents';

const PANEL_HEIGHT = 260;

const FILTER_TABS: { label: string; value: GlassBoxFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'LLM', value: 'llm' },
  { label: 'Tools', value: 'tools' },
  { label: 'Errors', value: 'errors' },
];

interface Props {
  onClose: () => void;
  filteredEvents: GlassBoxItem[];
  filter: GlassBoxFilter;
  onFilterChange: (f: GlassBoxFilter) => void;
  onClear: () => void;
  /** Spec 167 Phase 4: Load earlier events from Axiom */
  onLoadEarlier?: () => void;
  /** Whether backfill has been loaded */
  hasFullHistory?: boolean;
  /** Whether backfill is currently loading */
  isLoadingHistory?: boolean;
}

function EventRow({ item }: { item: GlassBoxItem }) {
  const name = item.event;
  if (name.includes('error')) return <ErrorEvent item={item} />;
  if (name.includes('llm')) return <LLMCallEvent item={item} />;
  if (name.includes('tool')) return <ToolCallEvent item={item} />;
  return <GenericObsEvent item={item} />;
}

export function GlassBoxPanel({
  onClose, filteredEvents, filter, onFilterChange, onClear,
  onLoadEarlier, hasFullHistory, isLoadingHistory,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new events
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filteredEvents.length]);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: PANEL_HEIGHT, opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="border-t border-surface-600 bg-surface-900 flex flex-col overflow-hidden"
      style={{ height: PANEL_HEIGHT }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-b border-surface-700 flex-shrink-0">
        <div className="flex items-center gap-1">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => onFilterChange(tab.value)}
              className={`text-xs px-2 py-0.5 rounded transition-colors ${
                filter === tab.value
                  ? 'bg-primary-500/20 text-primary-400'
                  : 'text-text-muted hover:text-text-secondary hover:bg-surface-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <span className="text-[10px] text-text-muted">
          {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
        </span>

        <button
          onClick={onClear}
          title="Clear events"
          className="p-1 rounded text-text-muted hover:text-text-secondary hover:bg-surface-800 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
        </button>

        <button
          onClick={onClose}
          title="Hide Glass Box"
          className="p-1 rounded text-text-muted hover:text-text-secondary hover:bg-surface-800 transition-colors"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Event list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-1">
        {/* Spec 167 Phase 4: Load earlier events from Axiom */}
        {onLoadEarlier && !hasFullHistory && (
          <button
            onClick={onLoadEarlier}
            disabled={isLoadingHistory}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-primary-400 hover:text-primary-300 hover:bg-surface-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-b border-surface-700"
          >
            {isLoadingHistory ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Loading history...</span>
              </>
            ) : (
              <>
                <History className="w-3 h-3" />
                <span>Load earlier events</span>
              </>
            )}
          </button>
        )}
        {filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-text-muted text-xs gap-1">
            <span>No events</span>
            <span className="text-[10px]">Events will appear when the agent loop runs</span>
          </div>
        ) : (
          filteredEvents.map(item => (
            <EventRow key={item.id} item={item} />
          ))
        )}
      </div>
    </motion.div>
  );
}
